"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelStateProxy = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
function cloneDrawableStates(source) {
    const drawableStates = mobx_1.observable.map();
    for (const [captureId, drawableState] of Array.from(source.entries())) {
        drawableStates.set(captureId, drawableState.clone());
    }
    return drawableStates;
}
class CollectionModelStateProxy {
    get loader() {
        return this.internalState.loader;
    }
    get id() {
        return this.internalState.id;
    }
    get instanceId() {
        return this.internalState.instanceId;
    }
    get uuid() {
        return this.internalState.uuid;
    }
    get revision() {
        return this.internalState.revision;
    }
    get jobId() {
        return this.internalState.jobId;
    }
    get updatedAt() {
        return this.internalState.updatedAt;
    }
    get updatedBy() {
        return this.internalState.updatedBy;
    }
    get createdAt() {
        return this.internalState.createdAt;
    }
    get collectedAt() {
        return this.internalState.collectedAt;
    }
    get collectedBy() {
        return this.internalState.collectedBy;
    }
    get collectedWith() {
        return this.internalState.collectedWith;
    }
    get assignedTo() {
        return this.internalState.assignedTo;
    }
    constructor(state) {
        this.frozen = false;
        /**
         * Drawable state is NOT proxied -- it starts as a full copy of the original state.
         */
        this.drawableStates = mobx_1.observable.map();
        this.internalState = state;
        this.resetDrawables();
    }
    get isDirty() {
        var _a;
        return ((_a = this.fields.size) !== null && _a !== void 0 ? _a : 0) > 0 || this.drawableStateHasChanged();
    }
    drawableStateHasChanged() {
        // Only compare the drawable states which have drawables in them,
        // because CollectionModelState may or may not have drawableStates for captures with no drawables,
        // but it always has drawable states for captures with drawables.
        const drawableStates = Array.from(this.drawableStates.values()).filter((ds) => !ds.empty);
        const internalDrawableStates = Array.from(this.internalState.drawableStates.values()).filter((ds) => !ds.empty);
        const keys = Array.from(drawableStates.keys());
        const internalKeys = Array.from(internalDrawableStates.keys());
        if (keys.length !== internalKeys.length) {
            return true;
        }
        if (keys.some((key) => !internalKeys.includes(key))) {
            return true;
        }
        if (internalKeys.some((key) => !keys.includes(key))) {
            return true;
        }
        for (const drawableState of drawableStates) {
            const originalDrawableState = internalDrawableStates.find((ds) => ds.captureId === drawableState.captureId);
            if (!originalDrawableState) {
                return true;
            }
            if (!drawableState.equals(originalDrawableState)) {
                return true;
            }
        }
        return false;
    }
    reset() {
        this.fields.clear();
        this.geometries.clear();
        this.resetDrawables();
    }
    resetDrawables() {
        this.drawableStates.clear();
        for (const [captureId, drawableState] of Array.from(cloneDrawableStates(this.internalState.drawableStates).entries())) {
            this.drawableStates.set(captureId, drawableState);
        }
    }
    getFieldValue(fieldId) {
        if (this.fields.has(fieldId)) {
            return this.fields.get(fieldId);
        }
        return this.internalState.getFieldValue(fieldId);
    }
    setFieldValue(fieldId, state) {
        let internalValue = this.internalState.getFieldValue(fieldId);
        if (JSON.stringify(state) === JSON.stringify(internalValue)) {
            this.fields.delete(fieldId);
        }
        else {
            this.fields.set(fieldId, state);
        }
    }
    hasFieldValue(fieldId) {
        return this.getFieldValue(fieldId) != null;
    }
    getDrawable(captureId, drawableId) {
        var _a;
        if ((_a = this.drawableStates[captureId]) === null || _a === void 0 ? void 0 : _a.hasDrawable(drawableId)) {
            return this.drawableStates[captureId].getDrawable(drawableId);
        }
        throw new Error(`Drawable ${drawableId} (capture ${captureId}) not found in proxy state`);
    }
    getCapture(captureId) {
        return this.internalState.getCapture(captureId);
    }
    getCalibration(calibrationId) {
        return this.internalState.getCalibration(calibrationId);
    }
    getGeometry(captureId) {
        return this.internalState.getGeometry(captureId);
    }
    getCollection(uuid) {
        return this.internalState.getCollection(uuid);
    }
    /**
     * @deprecated This method can create race conditions; use `getCollectionModelAsync()` instead.
     */
    getCollectionModel(collectionId) {
        return this.internalState.getCollectionModel(collectionId);
    }
    getCollectionModelAsync(collectionId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return yield this.internalState.getCollectionModelAsync(collectionId);
        });
    }
    freeze() {
        throw new Error('Cannot freeze edit state');
    }
}
exports.CollectionModelStateProxy = CollectionModelStateProxy;
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelStateProxy.prototype, "drawableStates", void 0);
//# sourceMappingURL=collection.state.proxy.js.map